import axios from 'axios';

// IMPORTANT: Use full URL for production API
const API_BASE = process.env.REACT_APP_API_URL || 'https://etlb.vwzz.eu/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' }
});

const MUTATING_METHODS = new Set(['post', 'patch', 'put', 'delete']);
const REALTIME_MUTATION_TIMEOUT_MS = Number(process.env.REACT_APP_REALTIME_MUTATION_TIMEOUT_MS || 8000);
const pendingRealtimeMutations = new Map();
const seenRealtimeMutations = [];
const localRealtimeMutations = new Set();
let realtimeSubscriptionCount = 0;

const getConfigPath = config => {
  const rawUrl = config?.url || '';
  try {
    return new URL(rawUrl, config?.baseURL || API_BASE).pathname.replace(/^\/api(?=\/)/, '');
  } catch {
    return String(rawUrl).split('?')[0];
  }
};

const shouldWaitForRealtimeMutation = config => {
  const method = String(config?.method || '').toLowerCase();
  if (!MUTATING_METHODS.has(method)) return false;
  const path = getConfigPath(config);
  return /^\/(flights|complaints|aircraft|mel)(?:\/|$)/.test(path);
};

const createClientMutationId = () => (
  `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
);

const rememberSeenMutation = mutationId => {
  if (!mutationId) return;
  seenRealtimeMutations.push(mutationId);
  if (seenRealtimeMutations.length > 200) seenRealtimeMutations.shift();
};

const rememberLocalMutation = mutationId => {
  if (!mutationId) return;
  localRealtimeMutations.add(mutationId);
  setTimeout(() => {
    localRealtimeMutations.delete(mutationId);
  }, REALTIME_MUTATION_TIMEOUT_MS + 30000);
};

const waitForRealtimeMutation = mutationId => {
  if (!mutationId || realtimeSubscriptionCount <= 0) return Promise.resolve();
  if (seenRealtimeMutations.includes(mutationId)) return Promise.resolve();

  return new Promise(resolve => {
    const timeout = setTimeout(() => {
      pendingRealtimeMutations.delete(mutationId);
      resolve();
    }, REALTIME_MUTATION_TIMEOUT_MS);

    pendingRealtimeMutations.set(mutationId, () => {
      clearTimeout(timeout);
      pendingRealtimeMutations.delete(mutationId);
      resolve();
    });
  });
};

const observeRealtimeEvent = event => {
  const mutationId = event?.client_mutation_id;
  if (!mutationId) return false;
  rememberSeenMutation(mutationId);
  const isLocalMutation = localRealtimeMutations.has(mutationId);
  pendingRealtimeMutations.get(mutationId)?.();
  return isLocalMutation;
};

// Attach JWT token to every request
api.interceptors.request.use(config => {
  const token = localStorage.getItem('etlb_token');
  console.log('[API] Request:', config.method?.toUpperCase(), config.url, '| Token:', token ? 'YES' : 'NO');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (shouldWaitForRealtimeMutation(config)) {
    const mutationId = createClientMutationId();
    rememberLocalMutation(mutationId);
    config.headers['X-Client-Mutation-Id'] = mutationId;
    config.etlbClientMutationId = mutationId;
  }
  return config;
});

// Handle 401 globally
api.interceptors.response.use(
  async res => {
    if (res.config?.etlbClientMutationId) {
      await waitForRealtimeMutation(res.config.etlbClientMutationId);
    }
    return res;
  },
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('etlb_token');
      localStorage.removeItem('etlb_user');
      localStorage.removeItem('etlb_aircraft');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// Auth
export const loginUser = (email, aircraftId, personalId) =>
  api.post('/auth/login', { email, aircraft_id: aircraftId, personal_id: personalId });

export const getMe = () => api.get('/auth/me');

export const subscribeToDataEvents = (onEvent, options = {}) => {
  const token = localStorage.getItem('etlb_token');
  if (!token || typeof EventSource === 'undefined') return () => {};

  realtimeSubscriptionCount += 1;
  const params = new URLSearchParams({ token });
  if (options.aircraftId) params.set('aircraft_id', options.aircraftId);
  if (options.flightId) params.set('flight_id', options.flightId);
  const eventUrl = `${API_BASE.replace(/\/$/, '')}/events?${params.toString()}`;
  const source = new EventSource(eventUrl, { withCredentials: true });

  source.addEventListener('change', event => {
    try {
      const parsedEvent = JSON.parse(event.data);
      const isLocalClientMutation = observeRealtimeEvent(parsedEvent);
      if (isLocalClientMutation) parsedEvent.isLocalClientMutation = true;
      onEvent(parsedEvent);
    } catch (err) {
      console.error('Realtime event parse error:', err);
    }
  });

  source.onerror = err => {
    console.error('Realtime event stream error:', err);
  };

  return () => {
    realtimeSubscriptionCount = Math.max(0, realtimeSubscriptionCount - 1);
    source.close();
  };
};

// Aircraft
export const getAllAircraft       = () => api.get('/aircraft');
export const getAircraft          = id => api.get(`/aircraft/${id}`);
export const getAircraftStatusReport = id => api.get(`/aircraft/${id}/status-report`);

// Flights
export const getUpcomingFlights   = aircraftId => api.get(`/flights/aircraft/${aircraftId}/upcoming`);
export const createFlight         = data => api.post('/flights', data);
export const getFlight            = id => api.get(`/flights/${id}`);
export const getFlights           = params => api.get('/flights', { params });
export const getFlightHistory     = params => api.get('/flights/history', { params });
export const updateFlightData     = (id, data) => api.patch(`/flights/${id}/flight-data`, data);
export const saveCrew             = (id, crew) => api.post(`/flights/${id}/crew`, { crew });
export const getFuel              = id => api.get(`/flights/${id}/fuel`);
export const saveFuel             = (id, data) => api.post(`/flights/${id}/fuel`, data);
export const saveOil              = (id, data) => api.post(`/flights/${id}/oil`, data);
export const addDelay             = (id, data) => api.post(`/flights/${id}/delay`, data);
export const updateDelay          = (id, delayId, data) => api.patch(`/flights/${id}/delay/${delayId}`, data);
export const removeDelay          = (id, delayId) => api.delete(`/flights/${id}/delay/${delayId}`);
export const saveSecuritySearch   = (id, data) => api.post(`/flights/${id}/security-search`, data);
export const saveDeicing          = (id, data) => api.post(`/flights/${id}/deicing`, data);
export const cancelFlight         = id => api.patch(`/flights/${id}/cancel`);
export const discardFlight        = id => api.patch(`/flights/${id}/discard`);
export const acceptAircraft       = (id, data = {}) => api.patch(`/flights/${id}/accept`, data);
export const revokeAcceptance     = id => api.patch(`/flights/${id}/revoke-acceptance`);
export const closeFlight          = (id, data = {}) => api.patch(`/flights/${id}/close`, data);
export const saveArrivalFuel      = (id, data) => api.post(`/flights/${id}/arrival-fuel`, data);
export const saveAutoland         = (id, data) => api.post(`/flights/${id}/autoland`, data);
export const getAirports          = () => api.get('/flights/airports/list');

// Admin
export const getAdminOverview     = () => api.get('/admin/overview');
export const getAdminHistory      = params => api.get('/admin/history', { params });
export const getAdminMostFlown    = params => api.get('/admin/most-flown', { params });
export const getAdminAircraft     = () => api.get('/admin/aircraft');
export const getAdminAircraftDetail = id => api.get(`/admin/aircraft/${id}/detail`);
export const getAdminUserDetail   = id => api.get(`/admin/users/${id}/detail`);
export const addAdminAircraft     = data => api.post('/admin/aircraft', data);
export const removeAdminAircraft  = id => api.delete(`/admin/aircraft/${id}`);
export const adminCancelFlight    = id => api.patch(`/admin/flights/${id}/cancel`);

// Complaints
export const getComplaints        = aircraftId => api.get(`/complaints/aircraft/${aircraftId}`);
export const createComplaint      = data => api.post('/complaints', data);
export const updateComplaint      = (id, data) => api.patch(`/complaints/${id}`, data);
export const getComplaint         = id => api.get(`/complaints/${id}`);
export const getComplaintActions  = id => api.get(`/complaints/${id}/actions`);
export const complaintActionsEnabled = process.env.REACT_APP_ENABLE_COMPLAINT_ACTIONS !== 'false';
export const getATAChapters       = () => api.get('/complaints/meta/ata-chapters');

// MEL Database
export const getMELTree           = () => api.get('/mel/tree');
export const getMELItems          = params => api.get('/mel/items', { params });
export const getMELItem           = reference => api.get(`/mel/items/${reference}`);
export const getMELProcedures     = params => api.get('/mel/procedures', { params });
export const getMELProcedure      = reference => api.get(`/mel/procedures/${reference}`);
export default api;
